/**
 * EIS e-invoice (HQ) page copy, English mirror of zh-CN/pages/eis.ts.
 * Key sets must stay identical (pinned by eisLabels.test.ts).
 */
export default {
  // ── Common ───────────────────────────────────────────────────────────
  'pages.eis.common.refresh': 'Refresh',
  'pages.eis.common.tenantFilterAll': 'All tenants',
  'pages.eis.common.updatedAt': 'Updated {time} · auto refresh every 5s',
  'pages.eis.common.detail': 'Detail',
  'pages.eis.common.pdf': 'PDF',
  'pages.eis.common.empty': 'No data',
  'pages.eis.common.goHandle': 'Resolve',
  'pages.eis.common.loading': 'Loading…',
  'pages.eis.common.copy': 'Copy doc no.',
  'pages.eis.common.copied': 'Copied',
  'pages.eis.common.save': 'Save',
  'pages.eis.common.unit': '',

  // ── Status map (dual caliber) ────────────────────────────────────────
  'pages.eis.status.upload.HELD': 'Held (transition)',
  'pages.eis.status.upload.PENDING': 'Pending upload',
  'pages.eis.status.upload.SUBMITTING': 'Uploading',
  'pages.eis.status.upload.SUBMITTED': 'Uploaded · awaiting result',
  'pages.eis.status.upload.ACCEPTED': 'Accepted by EIS',
  'pages.eis.status.upload.REJECTED': 'Rejected by EIS',
  'pages.eis.status.upload.FAILED': 'Upload failed',
  'pages.eis.status.upload.REVIEW': 'Needs review',
  'pages.eis.status.tenantUpload.HELD': 'Saved · EIS not live',
  'pages.eis.status.tenantUpload.PENDING': 'Queued',
  'pages.eis.status.tenantUpload.SUBMITTING': 'Pushing',
  'pages.eis.status.tenantUpload.SUBMITTED': 'Pushed · awaiting BIR',
  'pages.eis.status.tenantUpload.ACCEPTED': 'Pushed successfully',
  'pages.eis.status.tenantUpload.REJECTED': 'Push failed',
  'pages.eis.status.tenantUpload.FAILED': 'Push failed',
  'pages.eis.status.tenantUpload.REVIEW': 'Push failed · HQ handling',
  'pages.eis.status.email.NO_EMAIL': 'No email',
  'pages.eis.status.email.UNSENT': 'To send',
  'pages.eis.status.email.SENT': 'Sent',
  'pages.eis.status.email.FAILED': 'Send failed',
  'pages.eis.status.mode.HOLD': 'HOLD (transition)',
  'pages.eis.status.mode.LIVE': 'LIVE (production)',
  'pages.eis.status.health.OK': 'Normal',
  'pages.eis.status.health.ERROR': 'Suspended',
  'pages.eis.status.kind.SALE': 'Sales invoice',
  'pages.eis.status.kind.VOID': 'Void adjustment',
  'pages.eis.status.kind.RETURN': 'Return adjustment',
  'pages.eis.kind.code.SALE': '01',
  'pages.eis.kind.code.VOID': '06 / 04',
  'pages.eis.kind.code.RETURN': '06 / 05',
  'pages.eis.status.waitingOriginal': ' (awaiting original)',

  // ── Deadline four states ─────────────────────────────────────────────
  'pages.eis.deadline.uploaded': 'Uploaded',
  'pages.eis.deadline.heldExempt': 'Exempt (HOLD)',
  'pages.eis.deadline.overdue': 'Overdue {hours}h',
  'pages.eis.deadline.leftDays': '{days}d {hours}h left',
  'pages.eis.deadline.leftHours': '{hours}h {minutes}m left',

  // ── Event timeline, 21 keys ──────────────────────────────────────────
  'pages.eis.event.CREATED': 'e-Invoice generated',
  'pages.eis.event.SUBMITTED': 'Pushed to EIS (ack received)',
  'pages.eis.event.ACCEPTED': 'Accepted by EIS',
  'pages.eis.event.REJECTED': 'Rejected by EIS',
  'pages.eis.event.SUBMIT_FAILED': 'Push failed',
  'pages.eis.event.RESUBMIT_SCHEDULED': 'Batch unprocessable, re-pushing',
  'pages.eis.event.RESULT_MISSING': 'Result missing, re-pushing',
  'pages.eis.event.EMAIL_SENT': 'Email sent',
  'pages.eis.event.EMAIL_FAILED': 'Email send failed',
  'pages.eis.event.EMAIL_RESEND_REQUESTED': 'Email resend requested',
  'pages.eis.event.DEADLINE_DUE_SOON': 'Due soon (< 24h left)',
  'pages.eis.event.DEADLINE_OVERDUE': 'Overdue (> 3 days not uploaded)',
  'pages.eis.event.RELEASED': 'Transition release: queued for upload',
  'pages.eis.event.REGENERATED': 'Regenerated (new EIS ID)',
  'pages.eis.event.RESUBMIT_REQUESTED': 'Manual resubmit',
  'pages.eis.event.PRE_PTT_BLOCKED':
    'Original issued before PTT approval; adjustment not sent',
  'pages.eis.event.SUBMIT_LOST': 'Submit ack lost; auto retries exhausted',
  'pages.eis.event.RECONCILED': 'Daily reconciliation matched & cleared',
  'pages.eis.event.PRE_PTT_WRITTEN_OFF':
    'PRE-PTT adjustment written off locally',
  'pages.eis.event.UNPAID_HOLD': 'Unpaid order held',
  'pages.eis.event.RECONCILE_DIFF': 'Payment reconciliation diff logged',

  // ── Monitor overview ─────────────────────────────────────────────────
  'pages.eis.monitor.title': 'EIS Monitor',
  'pages.eis.monitor.subTitle':
    'e-Invoice upload & customer email status across all tenants; click a card, legend or attention row to filter the list',
  'pages.eis.monitor.unhealthyAlert':
    'EIS profile "{name}" ({cert}) push suspended: {msg}',
  'pages.eis.monitor.unhealthyAlertDesc':
    'Fix the credentials and run "Test connection" on the EIS Profile page to resume; documents stay queued meanwhile.',
  'pages.eis.monitor.deadlineAlert':
    '3-day upload deadline: {overdue}{dueSoon}',
  'pages.eis.monitor.deadlineAlert.overdue': '{count} overdue',
  'pages.eis.monitor.deadlineAlert.dueSoon': '{count} due within 24h',
  'pages.eis.monitor.deadlineAlertDesc':
    'No POS lockdown: the system keeps retrying and recording. Handle suspended profiles and rejected documents first. (Deadline events of release batches are aggregated per batch)',
  'pages.eis.monitor.deadlineAlert.action': 'View documents',
  'pages.eis.monitor.kpi.total': 'Total e-invoices',
  'pages.eis.monitor.kpi.totalHint': 'Amount total PHP {amount}',
  'pages.eis.monitor.kpi.accepted': 'Accepted by EIS',
  'pages.eis.monitor.kpi.acceptedHint': 'Accept rate {rate}%',
  'pages.eis.monitor.kpi.inflight': 'In flight',
  'pages.eis.monitor.kpi.inflightHint':
    'Pending {pending} · awaiting BIR {submitted}',
  'pages.eis.monitor.kpi.problem': 'Needs attention',
  'pages.eis.monitor.kpi.problemHint':
    'Rejected {rejected} · failed {failed} · review {review}',
  'pages.eis.monitor.overview.title': 'Upload status distribution',
  'pages.eis.monitor.overview.sub': '{total} in total',
  'pages.eis.monitor.overview.ACCEPTED': 'Accepted by EIS',
  'pages.eis.monitor.overview.SUBMITTED': 'Uploaded · awaiting result',
  'pages.eis.monitor.overview.PENDING': 'Pending upload',
  'pages.eis.monitor.overview.HELD': 'Held (transition)',
  'pages.eis.monitor.overview.REVIEW': 'Needs review',
  'pages.eis.monitor.overview.REJECTED': 'Rejected by EIS',
  'pages.eis.monitor.overview.FAILED': 'Upload failed',
  'pages.eis.monitor.attention.title': 'Attention',
  'pages.eis.monitor.attention.dueSoon': 'Due soon',
  'pages.eis.monitor.attention.dueSoonHint':
    'Less than 24h to the 3-day cutoff',
  'pages.eis.monitor.attention.overdue': 'Overdue',
  'pages.eis.monitor.attention.overdueHint':
    'Not uploaded in 3 days; recorded & alerted',
  'pages.eis.monitor.attention.mail': 'Email send failed',
  'pages.eis.monitor.attention.mailHint':
    'Edit email & resend in detail drawer',
  'pages.eis.monitor.attention.profile': 'EIS profile error',
  'pages.eis.monitor.attention.profileHint':
    'Credentials / IP allowlist issue, push suspended',
  'pages.eis.monitor.trend.title': 'Daily submit & accept trend',
  'pages.eis.monitor.trend.sub':
    'Last 14 days (PHT) · overall accept rate {rate}',
  'pages.eis.monitor.trend.tooltip':
    '{date} · submitted {submitted} | accepted {accepted} | rejected {rejected} | in flight {inflight}',
  'pages.eis.monitor.trend.accepted': 'Accepted (day)',
  'pages.eis.monitor.trend.rejected': 'Rejected (day)',
  'pages.eis.monitor.trend.inflight': 'In flight after submit',
  'pages.eis.monitor.errorDist.title': 'Result code distribution',
  'pages.eis.monitor.errorDist.sub': 'By document, incl. pending-review codes',
  'pages.eis.monitor.errorDist.empty': 'No errors',
  'pages.eis.monitor.table.title': 'e-Invoice documents',
  'pages.eis.monitor.quick.total': 'Total',
  'pages.eis.monitor.quick.ACCEPTED': 'Accepted by EIS',
  'pages.eis.monitor.quick.inflight': 'In flight',
  'pages.eis.monitor.quick.problem': 'Needs attention',
  'pages.eis.monitor.quick.HELD': 'Held (transition)',
  'pages.eis.monitor.quick.PENDING': 'Pending upload',
  'pages.eis.monitor.quick.SUBMITTED': 'Uploaded · awaiting result',
  'pages.eis.monitor.quick.REVIEW': 'Needs review',
  'pages.eis.monitor.quick.REJECTED': 'Rejected by EIS',
  'pages.eis.monitor.quick.FAILED': 'Upload failed',
  'pages.eis.monitor.quick.dueSoon': 'Due soon',
  'pages.eis.monitor.quick.overdue': 'Overdue',
  'pages.eis.monitor.quick.emailFailed': 'Email send failed',
  'pages.eis.monitor.quick.reconciliation': 'Pending reconciliation',
  'pages.eis.monitor.quick.resultCode': 'Result code {code}',

  // ── Document columns (one field per column) ──────────────────────────
  'pages.eis.column.sourceNo': 'Doc No.',
  'pages.eis.column.docKind': 'Doc type',
  'pages.eis.column.siNumber': 'Original SI No.',
  'pages.eis.column.tenant': 'Tenant',
  'pages.eis.column.store': 'Store',
  'pages.eis.column.accreditationId': 'EIS Cert No.',
  'pages.eis.column.orderCreatedAt': 'Order created (PHT)',
  'pages.eis.column.issueDtm': 'Issued (PHT)',
  'pages.eis.column.netAmtPay': 'Amount (PHP)',
  'pages.eis.column.uploadStatus': 'Upload status',
  'pages.eis.column.resultCode': 'Result code',
  'pages.eis.column.deadline': '3-day deadline',
  'pages.eis.column.arrivedLate': 'POS late',
  'pages.eis.column.emailStatus': 'Email status',
  'pages.eis.column.customerEmail': 'Customer email',
  'pages.eis.column.action': 'Actions',
  'pages.eis.search.tenant': 'Tenant',
  'pages.eis.search.store': 'Store',
  'pages.eis.search.q': 'Doc No. / EIS ID',
  'pages.eis.search.qPlaceholder': '16-digit doc no. / SI no. / EIS ID',
  'pages.eis.search.kind': 'Doc type',
  'pages.eis.search.resultCode': 'Result code',
  'pages.eis.search.resultCodePlaceholder': 'e.g. SYN003',
  'pages.eis.search.reconciliation': 'Pending reconciliation',
  'pages.eis.search.reconciliationOnly':
    'Only documents pending reconciliation',
  'pages.eis.search.allTenants': 'All tenants',
  'pages.eis.search.allStores': 'All stores',
  'pages.eis.posLate.tooltip':
    'POS offline: the order reached the cloud more than 3 days late (B-27: POS-side responsibility, flag only)',
  'pages.eis.posLate.tag': 'Late',

  // ── Data center (R41) ────────────────────────────────────────────────
  'pages.eis.dataCenter.title': 'Data Center',
  'pages.eis.dataCenter.subTitle':
    '{tenants} tenants · {bound} / {stores} stores bound to an EIS profile. Each merchant applies for its own EIS Cert & PTT; stores of one tenant may bind different cert numbers',
  'pages.eis.dataCenter.subTitleLoading': 'Bind EIS profiles by tenant → store',
  'pages.eis.dataCenter.tenantBound': '{bound} / {total} stores bound',
  'pages.eis.dataCenter.metric.total': 'e-Invoices',
  'pages.eis.dataCenter.metric.accepted': 'Accepted',
  'pages.eis.dataCenter.metric.held': 'Held',
  'pages.eis.dataCenter.metric.problem': 'Attention',
  'pages.eis.dataCenter.profiles': 'EIS profiles',
  'pages.eis.dataCenter.profile.manage': 'Manage profiles',
  'pages.eis.dataCenter.profile.create': 'Create EIS profile',
  'pages.eis.dataCenter.profile.paused': 'Suspended',
  'pages.eis.dataCenter.profile.live': 'Production',
  'pages.eis.dataCenter.profile.hold': 'Transition',
  'pages.eis.dataCenter.column.store': 'Store',
  'pages.eis.dataCenter.column.storeId': 'Store ID',
  'pages.eis.dataCenter.column.binding': 'Bound EIS profile',
  'pages.eis.dataCenter.column.bindingPlaceholder':
    'Select a profile of this tenant',
  'pages.eis.dataCenter.column.sellerType': 'Seller Type',
  'pages.eis.dataCenter.column.branchCd': 'BranchCd',
  'pages.eis.dataCenter.column.branchCdTooltip':
    'Leave empty to parse from the order TIN suffix (e.g. 444-867-786-001 → 00001)',
  'pages.eis.dataCenter.column.branchCdPlaceholder': 'Auto parse',
  'pages.eis.dataCenter.column.sellerEmail': 'Seller email',
  'pages.eis.dataCenter.column.sellerEmailPlaceholder': 'Optional',
  'pages.eis.dataCenter.column.bound': 'Binding',
  'pages.eis.dataCenter.column.boundYes': 'Bound',
  'pages.eis.dataCenter.column.boundNo': 'Unbound',
  'pages.eis.dataCenter.column.pendingGenerate': 'To generate',
  'pages.eis.dataCenter.column.pendingGenerateTooltip':
    'Uploaded e-invoice orders not yet generated because the store is unbound; generated automatically once the binding is saved',
  'pages.eis.dataCenter.column.action': 'Actions',
  'pages.eis.dataCenter.save': 'Save',
  'pages.eis.dataCenter.saveChanges': 'Save changes',
  'pages.eis.dataCenter.unbind': 'Unbind',
  'pages.eis.dataCenter.unbindConfirm':
    'After unbinding, new e-invoice orders of this store will not be generated. Continue?',
  'pages.eis.dataCenter.msg.saved':
    'Binding saved, {count} e-invoice(s) generated',
  'pages.eis.dataCenter.msg.savedNoBackfill': 'Binding saved',
  'pages.eis.dataCenter.msg.unbound': 'Unbound',
  'pages.eis.dataCenter.empty': 'No tenants',
  'pages.eis.sellerType.0': '0 VAT',
  'pages.eis.sellerType.1': '1 Non-VAT',
  'pages.eis.sellerType.2': '2 RBE',

  // ── EIS profile page ─────────────────────────────────────────────────
  'pages.eis.profile.title': 'EIS Profiles',
  'pages.eis.profile.subTitle':
    'Each merchant applies for its own EIS Cert & PTT; one card = one EIS Cert Number. Bind stores in the Data Center',
  'pages.eis.profile.create': 'New profile',
  'pages.eis.profile.group.count': '{count} profile(s)',
  'pages.eis.profile.health.ok': 'Connected',
  'pages.eis.profile.health.error': 'Push suspended',
  'pages.eis.profile.certLabel': 'EIS Cert Number',
  'pages.eis.profile.kv.applicationId': 'Application ID',
  'pages.eis.profile.kv.env': 'Environment',
  'pages.eis.profile.kv.envSandbox': 'Sandbox',
  'pages.eis.profile.kv.envProd': 'Production',
  'pages.eis.profile.kv.voidCorrectionCd': 'Void correction code',
  'pages.eis.profile.kv.voidCd04': '04 Cancellation',
  'pages.eis.profile.kv.voidCd01': '01 Error',
  'pages.eis.profile.kv.pttApprovedOn': 'PTT approved on',
  'pages.eis.profile.kv.pttNotApproved': 'Not approved',
  'pages.eis.profile.kv.credentialUpdatedAt': 'Credentials updated',
  'pages.eis.profile.kv.baseUrl': 'API base URL',
  'pages.eis.profile.stores.empty':
    'No store bound yet; bind in the Data Center',
  'pages.eis.profile.action.test': 'Test connection',
  'pages.eis.profile.action.testing': 'Testing…',
  'pages.eis.profile.action.testTooltip':
    'Call the Authentication API to verify credentials',
  'pages.eis.profile.action.selftest': 'Sandbox self-test',
  'pages.eis.profile.action.selftestTooltip':
    'Run the portal sandbox test in one click: auth ×2, issue ×1, inquiry ×2; results archived as admission evidence (R49)',
  'pages.eis.profile.action.toLive': 'Go LIVE',
  'pages.eis.profile.action.toHold': 'Back to HOLD',
  'pages.eis.profile.action.toLiveConfirm':
    'After switching to LIVE, new documents are uploaded to EIS directly and the 3-day deadline applies. Continue?',
  'pages.eis.profile.action.toHoldConfirm':
    'After switching back to HOLD, new documents are stored without uploading; in-flight documents return to HELD and stop counting the 3-day deadline. Continue?',
  'pages.eis.profile.action.release': 'Release held',
  'pages.eis.profile.action.edit': 'Edit',
  'pages.eis.profile.modeInfo.title': 'Mode notes',
  'pages.eis.profile.modeInfo.desc':
    'HOLD = transition period: generate & store e-invoices without calling EIS; LIVE = production push (3-day deadline applies). After EIS goes live, switch to LIVE first, then use "Release held" to queue backlogged documents by date range; with PTT approval date set, the release preview reports how many documents predate it.',
  'pages.eis.profile.msg.switched': 'Switched',
  'pages.eis.profile.msg.saved': 'Saved',
  'pages.eis.profile.test.ok': 'Connection OK',
  'pages.eis.profile.test.fail': 'Connection failed',

  // ── ProfileForm ──────────────────────────────────────────────────────
  'pages.eis.form.createTitle': 'Create EIS profile',
  'pages.eis.form.editTitle': 'Edit EIS profile: {name}',
  'pages.eis.form.tenantId': 'Tenant',
  'pages.eis.form.tenantIdPlaceholder': 'Select tenant',
  'pages.eis.form.name': 'Profile name',
  'pages.eis.form.namePlaceholder': 'e.g. Makati POS accreditation',
  'pages.eis.form.accreditationId': 'EIS Cert Number',
  'pages.eis.form.accreditationIdTooltip':
    'Sent as the accreditationId header and positions 9-16 of EisUniqueId. POS starts with 31, ASP-POS with 52',
  'pages.eis.form.accreditationIdRule': '8 digits or uppercase letters',
  'pages.eis.form.required': 'Required',
  'pages.eis.form.applicationId': 'Application ID',
  'pages.eis.form.userId': 'Portal User ID',
  'pages.eis.form.password': 'Portal password',
  'pages.eis.form.appSecret': 'App Secret Key',
  'pages.eis.form.signKeyId': 'Signing Key-pair ID',
  'pages.eis.form.signKeyIdTooltip': 'kid in the JWS header',
  'pages.eis.form.signPrivateKey': 'Signing private key (PEM)',
  'pages.eis.form.signPrivateKeyPlaceholder': '-----BEGIN PRIVATE KEY-----',
  'pages.eis.form.eisPublicKey': 'EIS public key',
  'pages.eis.form.eisPublicKeyPlaceholder':
    'Empty = built-in mock key; production uses EIS_Certification_Public_Key.txt',
  'pages.eis.form.baseUrl': 'API base URL',
  'pages.eis.form.baseUrlPlaceholder':
    'Empty = built-in sandbox; sandbox https://eis-cert.bir.gov.ph',
  'pages.eis.form.apiPrefix': 'API prefix',
  'pages.eis.form.env': 'Environment',
  'pages.eis.form.mode': 'Mode',
  'pages.eis.form.modeHOLD': 'HOLD transition',
  'pages.eis.form.modeLIVE': 'LIVE production',
  'pages.eis.form.minInterval': 'Min invoice interval',
  'pages.eis.form.minIntervalTooltip':
    'Sandbox allows 1 invoice call per 10s: enter 10000; production 0',
  'pages.eis.form.voidCorrectionCd': 'Void correction code',
  'pages.eis.form.voidCorrectionCdTooltip':
    'CorrectionCd of void adjustments (DocType 06)',
  'pages.eis.form.pttApprovedOn': 'PTT approved on',
  'pages.eis.form.pttApprovedOnTooltip':
    'EIS rule: originals issued before this date must not carry correction documents; the release preview reports how many predate it',
  'pages.eis.form.keepHint': 'Leave empty = keep current',
  'pages.eis.form.credentialSet': 'Configured (leave empty = keep)',

  // ── ReleaseModal ─────────────────────────────────────────────────────
  'pages.eis.release.title': 'Release held documents: {name} ({cert})',
  'pages.eis.release.range': 'Issue date',
  'pages.eis.release.rangeTooltip':
    'Empty = unlimited; release in date slices for huge backlogs (B-08)',
  'pages.eis.release.notLive':
    'Still in HOLD mode: released documents will not be pushed. Switch to LIVE first.',
  'pages.eis.release.preview': 'Preview count',
  'pages.eis.release.execute': 'Execute release',
  'pages.eis.release.previewInfo':
    '{count} held document(s) in this range will be released',
  'pages.eis.release.prePttWarning':
    '{count} of them were issued before the PTT approval date ({date})',
  'pages.eis.release.prePttDesc':
    'Whether BIR accepts backfilled e-invoices issued before PTT approval is unconfirmed: release as usual and verify in sandbox; results follow EIS receipts. Void/return adjustments of these originals are not sent and go to manual review.',
  'pages.eis.release.msg.done': '{count} document(s) queued for upload',

  // ── SelfTestModal (R49) ──────────────────────────────────────────────
  'pages.eis.selftest.title': 'Sandbox self-test: {name} ({cert})',
  'pages.eis.selftest.intro':
    'Verifies 5 online steps per the EIS portal admission requirement: auth ×2 (incl. forced token refresh) → submit 1 test invoice → inquiry ×2. The test invoice amounts to 112 PHP, registered on the EIS side only and never enters business documents; results are archived as per-merchant admission evidence.',
  'pages.eis.selftest.running':
    'Running self-test (the real sandbox enforces a minimum call interval, may be slow)…',
  'pages.eis.selftest.rerun': 'Re-run',
  'pages.eis.selftest.close': 'Close',
  'pages.eis.selftest.step.passed': 'Passed',
  'pages.eis.selftest.step.failed': 'Failed',
  'pages.eis.selftest.step.noResult': 'No result',
  'pages.eis.selftest.step.skipped': 'Skipped',
  'pages.eis.selftest.result.allPassed':
    'All steps passed: credentials, signing, submit & inquiry links work. Screenshot as admission evidence',
  'pages.eis.selftest.result.failed':
    'Self-test failed ({step}): fix the profile per the hints above and retry',
  'pages.eis.selftest.result.noResult':
    'Submitted but EIS has no result yet: sandbox results are usually queryable the next day; re-run later',
  'pages.eis.selftest.history': 'History ({count})',
  'pages.eis.selftest.historySteps': '{passed}/{total} steps passed',
  'pages.eis.selftest.historyPass': 'Passed',
  'pages.eis.selftest.historyFail': 'Failed',

  // ── Document drawer (hq mode) ────────────────────────────────────────
  'pages.eis.drawer.title': '{kind} detail',
  'pages.eis.drawer.resubmit': 'Manual resubmit',
  'pages.eis.drawer.regenerate': 'Regenerate',
  'pages.eis.drawer.prevSi': 'Original SI {no}',
  'pages.eis.drawer.tenantStore': '{tenant} / {store}',
  'pages.eis.drawer.amountLabel': 'Amount PHP',
  'pages.eis.drawer.tab.overview': 'Overview',
  'pages.eis.drawer.tab.json': 'EIS payload',
  'pages.eis.drawer.tab.submissions': 'Submissions ({count})',
  'pages.eis.drawer.tab.events': 'Events ({count})',
  'pages.eis.drawer.validationAlert':
    'Local pre-validation failed; not sent to EIS',
  'pages.eis.drawer.desc.eisUniqueId': 'BIR e-Invoice ID',
  'pages.eis.drawer.desc.prevUniqueId': 'Original EIS ID',
  'pages.eis.drawer.desc.prevEisUniqueId': 'Original EIS ID (re-numbered)',
  'pages.eis.drawer.desc.orderCreatedAt': 'Order created',
  'pages.eis.drawer.desc.issueDtm': 'Issued at',
  'pages.eis.drawer.desc.deadlineAt': 'Upload deadline',
  'pages.eis.drawer.desc.deadlineState': 'Deadline state',
  'pages.eis.drawer.desc.result': 'BIR result',
  'pages.eis.drawer.desc.accreditationId': 'EIS Cert No.',
  'pages.eis.drawer.desc.attemptCount': 'Submit attempts',
  'pages.eis.drawer.desc.receivedAt': 'Received by cloud',
  'pages.eis.drawer.desc.receivedLate':
    ' (POS late: offline > 3 days before sync)',
  'pages.eis.drawer.desc.reconciliation': 'Reconciliation',
  'pages.eis.drawer.desc.reconciliationFlagged': 'Pending reconciliation',
  'pages.eis.drawer.desc.reconciliationClean': 'Reconciled',
  'pages.eis.drawer.email.section': 'Customer email',
  'pages.eis.drawer.email.placeholder': 'Customer email',
  'pages.eis.drawer.email.send': 'Save & send',
  'pages.eis.drawer.email.resend': 'Resend',
  'pages.eis.drawer.email.lastError': 'Last failure: {msg}',
  'pages.eis.drawer.email.queued': 'Queued for sending',
  'pages.eis.drawer.resubmit.queued': 'Queued for resubmit',
  'pages.eis.drawer.regenerate.done': 'Regenerated with current mapping',
  'pages.eis.drawer.json.desc':
    'Frozen plaintext JSON (before signing/encryption) · SHA-256 {sha}',
  'pages.eis.drawer.submissions.submitId': 'submitId',
  'pages.eis.drawer.submissions.ackId': 'ackId',
  'pages.eis.drawer.submissions.processStatus': 'Process status',
  'pages.eis.drawer.submissions.submittedAt': 'Submitted (PHT)',
  'pages.eis.drawer.submissions.empty': 'Not submitted yet',
  'pages.eis.drawer.submissions.01': 'Completed',
  'pages.eis.drawer.submissions.02': 'Processing',
  'pages.eis.drawer.submissions.03': 'Unprocessable',
  'pages.eis.drawer.steps.generate': 'Generate',
  'pages.eis.drawer.steps.generate.done': 'JSON generated',
  'pages.eis.drawer.steps.generate.review': 'Needs review',
  'pages.eis.drawer.steps.push': 'Push to EIS',
  'pages.eis.drawer.steps.push.held': 'Held (transition)',
  'pages.eis.drawer.steps.push.review': 'Not sent',
  'pages.eis.drawer.steps.push.acked': 'Ack received',
  'pages.eis.drawer.steps.push.failed': 'Push failed',
  'pages.eis.drawer.steps.push.queued': 'Queued',
  'pages.eis.drawer.steps.confirm': 'BIR confirm',
  'pages.eis.drawer.steps.confirm.processing': 'Processing',
  'pages.eis.drawer.steps.confirm.none': '—',
  'pages.eis.drawer.steps.confirm.rejected': 'Rejected',
  'pages.eis.drawer.steps.mail': 'Email delivery',
  'pages.eis.drawer.event.operator': 'by {name}',
  'pages.eis.drawer.event.submitId': 'submitId {id}',
  'pages.eis.drawer.event.ackId': 'ackId {id}',
  'pages.eis.drawer.event.to': '→ {to}',
  'pages.eis.drawer.event.newUniqueId': 'new EIS ID {id}',
} as const;
